import { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";

import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";

import { it } from "date-fns/locale";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import { cardStyle, colors, emptyBoxStyle } from "../ui/styles";
import API from "../services/api";

const DragAndDropCalendar = withDragAndDrop(Calendar);

const locales = { it };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const culture = "it";

function CalendarPage({ onOpenPlant }) {


  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventoSelezionato, setEventoSelezionato] = useState(null);

  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");

  const [showMaintenance, setShowMaintenance] = useState(true);
  const [showDeadlines, setShowDeadlines] = useState(true);
  const [showInterventions, setShowInterventions] = useState(true);

  const token = localStorage.getItem("token");

  const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString("it-IT");
  };

  const getTipoEvento = (type) => {
    if (type === "maintenance") return "Manutenzione";
    if (type === "deadline") return "Scadenza tecnica";
    if (type === "intervention") return "Intervento";
    return "Evento";
  };

  const getEventColor = (type) => {
    if (type === "maintenance") return "#d97706";
    if (type === "deadline") return "#dc2626";
    if (type === "intervention") return "#16a34a";
    return colors.primary;
  };

  const getPriorityLabel = (priority) => {
    if (priority === "critical") return "🔴 Critica";
    if (priority === "warning") return "🟡 Da monitorare";
    if (priority === "done") return "🟢 Completato";
    return "🟢 Regolare";
  };

  const caricaCalendario = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/calendar/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Errore caricamento calendario");
      }

      const data = await response.json();

      const calendarEvents = data.map((item) => ({
        id: item.id,
        title: `${item.label}: ${item.title}`,
        start: new Date(item.date),
        end: new Date(item.date),
        type: item.type,
        priority: item.priority,
        resource: item,
      }));

      setEvents(calendarEvents);
    } catch (err) {
      console.error(err);
      alert("Errore caricamento calendario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    caricaCalendario();
  }, []);

  const handleEventDrop = async ({ event, start }) => {
    try {
      const sourceId = event.resource?.source_id;
      const eventType = event.resource?.type;

      if (!sourceId || !eventType) {
        alert("Evento non spostabile");
        return;
      }

      const newDate = format(start, "yyyy-MM-dd");

      const response = await fetch(
        `${API}/calendar/events/${eventType}/${sourceId}/move`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            date: newDate,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Errore spostamento evento");
      }

      setEventoSelezionato(null);
      await caricaCalendario();
    } catch (err) {
      console.error(err);
      alert("Errore durante lo spostamento dell'evento");
      await caricaCalendario();
    }
  };

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (event.type === "maintenance") return showMaintenance;
      if (event.type === "deadline") return showDeadlines;
      if (event.type === "intervention") return showInterventions;
      return true;
    });
  }, [events, showMaintenance, showDeadlines, showInterventions]);

  const eventStyleGetter = (event) => {
    let backgroundColor = getEventColor(event.type);

    if (event.priority === "critical") backgroundColor = "#dc2626";
    if (event.priority === "warning") backgroundColor = "#d97706";
    if (event.priority === "done") backgroundColor = "#16a34a";

    return {
      style: {
        backgroundColor,
        borderRadius: "8px",
        border: "none",
        color: "#fff",
        padding: "2px 6px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "grab",
      },
    };
  };

  const messages = useMemo(
    () => ({
      today: "Oggi",
      previous: "←",
      next: "→",
      month: "Mese",
      week: "Settimana",
      day: "Giorno",
      agenda: "Agenda",
      date: "Data",
      time: "Ora",
      event: "Evento",
      noEventsInRange: "Nessun evento nel periodo selezionato",
    }),
    []
  );

  const filtroButton = ({ color, label, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cardStyle,
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        border: active ? `2px solid ${color}` : "1px solid #dbe4f0",
        opacity: active ? 1 : 0.45,
        cursor: "pointer",
        background: active ? "#fff" : "#f8fafc",
        fontWeight: active ? 700 : 500,
      }}
    >
      <div
        style={{
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: color,
        }}
      />
      <span>
        {active ? "✓ " : ""}
        {label}
      </span>
    </button>
  );

  return (
    <>
      <div style={{ marginBottom: "26px" }}>
        <h1
          style={{
            color: colors.title,
            fontSize: "32px",
            fontWeight: 400,
            marginBottom: "8px",
          }}
        >
          Calendario operativo
        </h1>

        <p style={{ color: colors.muted, fontSize: "16px" }}>
          Pianificazione manutenzioni, interventi e scadenze tecniche
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "14px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        {filtroButton({
          color: "#d97706",
          label: "Manutenzioni",
          active: showMaintenance,
          onClick: () => setShowMaintenance(!showMaintenance),
        })}

        {filtroButton({
          color: "#dc2626",
          label: "Scadenze tecniche",
          active: showDeadlines,
          onClick: () => setShowDeadlines(!showDeadlines),
        })}

        {filtroButton({
          color: "#16a34a",
          label: "Interventi",
          active: showInterventions,
          onClick: () => setShowInterventions(!showInterventions),
        })}
      </div>

      <div
  style={{
    ...cardStyle,
    height: "650px",
    minHeight: "650px",
    padding: "18px",
    overflow: "hidden",
  }}
>
        {loading ? (
          <p>Caricamento calendario...</p>
        ) : (
          <DragAndDropCalendar
            localizer={localizer}
            events={filteredEvents}
            culture={culture}
            date={calendarDate}
            view={calendarView}
            onNavigate={(newDate) => setCalendarDate(newDate)}
            onView={(newView) => setCalendarView(newView)}
            startAccessor="start"
            endAccessor="end"
            style={{
  height: "100%",
  minHeight: "610px",
}}
            messages={messages}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setEventoSelezionato(event)}
            onEventDrop={handleEventDrop}
            draggableAccessor={() => true}
            popup
          />
        )}
      </div>

      {eventoSelezionato && (
        <div style={{ ...cardStyle, marginTop: "24px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
              marginBottom: "18px",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  padding: "6px 10px",
                  borderRadius: "999px",
                  background: getEventColor(eventoSelezionato.type),
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  marginBottom: "10px",
                }}
              >
                {getTipoEvento(eventoSelezionato.type)}
              </div>

              <h2
                style={{
                  color: colors.title,
                  margin: 0,
                  fontSize: "24px",
                }}
              >
                {eventoSelezionato.title}
              </h2>
            </div>

            <button
              className="btn btn-secondary"
              onClick={() => setEventoSelezionato(null)}
              style={{
                background: "#e2e8f0",
                border: "none",
                borderRadius: "8px",
                padding: "10px 14px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Chiudi
            </button>
          </div>

          <div
            style={{
              ...emptyBoxStyle,
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "12px 18px",
            }}
          >
            <div>
              <strong>Data:</strong> {formatDate(eventoSelezionato.start)}
            </div>

            <div>
              <strong>Cliente:</strong>{" "}
              {eventoSelezionato.resource?.customer_name || "—"}
            </div>

            <div>
              <strong>Impianto:</strong>{" "}
              {eventoSelezionato.resource?.plant_name || "—"}
            </div>

            <div>
              <strong>Stato:</strong>{" "}
              {eventoSelezionato.resource?.status || "—"}
            </div>

            <div>
              <strong>Priorità:</strong>{" "}
              {getPriorityLabel(eventoSelezionato.resource?.priority)}
            </div>

            <div>
              <strong>Tecnico:</strong>{" "}
              {eventoSelezionato.resource?.technician_name || "—"}
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <strong>Note:</strong>{" "}
              {eventoSelezionato.resource?.notes ||
                eventoSelezionato.resource?.outcome ||
                "—"}
            </div>

            <div style={{ gridColumn: "1 / -1", marginTop: "10px" }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (eventoSelezionato.resource?.plant_id) {
                    onOpenPlant(eventoSelezionato.resource.plant_id);
                  }
                }}
                style={{
                  background: colors.primary,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Apri impianto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CalendarPage;